/**
 * React bindings for task lists. Separate from taskDocs.ts so the module
 * graph stays acyclic: this file imports both the manager (taskDocs) and the
 * context registry (store).
 */
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { setTaskDone, type TaskItem, type WorkspaceItem } from "@hydra-notes/shared";
import { ctxFor, getData, useAllSpaceItems } from "./store";
import type { TaskHandle } from "./taskDocs";

const EMPTY_TASKS: TaskItem[] = [];

/** Open one list's live doc for the component's lifetime. */
export function useTaskList(
  listId: string,
  spaceId?: string,
): { tasks: TaskItem[]; loaded: boolean; handle: TaskHandle | null } {
  const [handle, setHandle] = useState<TaskHandle | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const ctx = spaceId ? ctxFor(spaceId) : getData();
    if (!ctx || ctx.destroyed) return;
    const h = ctx.tasks.open(listId);
    setHandle(h);
    setLoaded(false);
    let alive = true;
    void h.whenLoaded.then(() => {
      if (alive) setLoaded(true);
    });
    return () => {
      alive = false;
      h.release();
      setHandle(null);
    };
  }, [listId, spaceId]);

  const tasks = useSyncExternalStore(
    handle ? handle.store.subscribe : noopSubscribe,
    handle ? handle.store.getSnapshot : () => EMPTY_TASKS,
  );
  return { tasks, loaded, handle };
}

function noopSubscribe(): () => void {
  return () => {};
}

export interface TaskListMeta {
  listId: string;
  spaceId: string;
  spaceKind: "personal" | "shared";
  title: string;
  colorKey?: string;
  builtin: boolean;
}

export interface AllTaskRow {
  task: TaskItem;
  list: TaskListMeta;
}

/** Every live task list across all mounted spaces. */
export function useTaskLists(): TaskListMeta[] {
  const spaces = useAllSpaceItems();
  return useMemo(() => {
    const out: TaskListMeta[] = [];
    for (const s of spaces) {
      for (const i of s.items) {
        if (i.type !== "tasklist" || i.trashedAt || i.archivedAt) continue;
        out.push({
          listId: i.id,
          spaceId: s.spaceId,
          spaceKind: s.kind,
          title: i.title || "Untitled",
          colorKey: i.colorKey,
          builtin: i.builtin === "tasks",
        });
      }
    }
    return out;
  }, [spaces]);
}

/**
 * Every task across every mounted list. Holds the lists' docs open while the
 * consuming component (the Tasks panel) is mounted; a handful of lists is
 * cheap, and live docs are the only source that works offline.
 */
export function useAllTasks(): { rows: AllTaskRow[]; loaded: boolean } {
  const lists = useTaskLists();
  const [state, setState] = useState<{ rows: AllTaskRow[]; loaded: boolean }>({ rows: [], loaded: false });
  const key = lists.map((l) => `${l.spaceId}/${l.listId}`).join(",");

  useEffect(() => {
    let alive = true;
    const opened: { list: TaskListMeta; handle: TaskHandle }[] = [];
    for (const list of lists) {
      const ctx = ctxFor(list.spaceId);
      if (!ctx || ctx.destroyed) continue;
      opened.push({ list, handle: ctx.tasks.open(list.listId) });
    }
    const recompute = (loaded: boolean) => {
      if (!alive) return;
      setState({
        rows: opened.flatMap(({ list, handle }) => handle.store.getSnapshot().map((task) => ({ task, list }))),
        loaded,
      });
    };
    const unsubs = opened.map(({ handle }) => handle.store.subscribe(() => recompute(true)));
    recompute(false);
    void Promise.all(opened.map(({ handle }) => handle.whenLoaded)).then(() => recompute(true));
    return () => {
      alive = false;
      for (const u of unsubs) u();
      for (const { handle } of opened) handle.release();
    };
    // key captures list identity; lists itself is a new array each snapshot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

/**
 * Toggle a task's done state from anywhere (panel, dashboard) without a
 * mounted list view. The doc is usually already open; the open/release pair
 * just rides the refcount.
 */
export function toggleTaskDone(spaceId: string, listId: string, taskId: string, done: boolean, userId: string): boolean {
  const ctx = ctxFor(spaceId);
  if (!ctx || ctx.destroyed) return false;
  const h = ctx.tasks.open(listId);
  setTaskDone(h.doc, taskId, done, userId);
  ctx.workspace.touch(listId);
  h.release();
  return true;
}

/** The item metadata of the default task list (personal space). */
export function defaultTaskListOf(items: WorkspaceItem[]): WorkspaceItem | undefined {
  const candidates = items.filter((i) => i.type === "tasklist" && i.builtin === "tasks" && !i.trashedAt);
  candidates.sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
  return candidates[0];
}
